import {
  AlbumCommonDefaultProvider,
  AnyTeamEntityVersion,
  AnyTeamGroupEntityVersion,
  AnyUserEntityVersion,
  AuthCommonDefaultProvider,
  Cached,
  Collections,
  Team,
  TeamCommonDefaultProvider,
} from '@uploadcam/common';
import { CollectionReference, Firestore } from 'firebase/firestore';
import { Functions } from 'firebase/functions';
import { AuthProvider } from '../auth/auth.provider';
import { AcceptInviteInteractor } from './domain/interactors/accept-invite.interactor';
import { AddTeamMemberInteractor } from './domain/interactors/add-team-member.interactor';
import { CreateTeamInteractor } from './domain/interactors/create-team.interactor';
import { GetTeamByIdInteractor } from './domain/interactors/get-team-by-id.interactor';
import { GetTeamUsersInteractor } from './domain/interactors/get-team-users.interactor';
import { LeaveTeamInteractor } from './domain/interactors/leave-team.interactor';
import { UpdateUserInteractor } from './domain/interactors/update-user.interactor';
import { UpdateUserLocalInteractor } from './domain/interactors/update-user-local.interactor';
import { RemoveTeamMemberInteractor } from './domain/interactors/remove-team-member.interactor';
import { UpdateTeamInteractor } from './domain/interactors/update-team.interactor';
import { CanEnableDomainInteractor } from './domain/interactors/can-enable-domain.interactor';
import { DisableDomainInteractor } from './domain/interactors/disable-domain.interactor';
import { EnableDomainInteractor } from './domain/interactors/enable-domain.interactor';
import { GetTeamSizeInteractor } from './domain/interactors/get-team-size.interactor';
import { UpdateTeamSettingsInteractor } from './domain/interactors/update-team-settings.interactor';
import { getCollectionRef } from '../../@harmony/angular';
import { CreateTeamGroupInteractor } from './domain/interactors/create-team-group.interactor';
import { GetTeamGroupsInteractor } from './domain/interactors/get-team-groups.interactor';
import { UpdateTeamGroupInteractor } from './domain/interactors/update-team-group.interactor';
import { DeleteTeamGroupInteractor } from './domain/interactors/delete-team-group.interactor';
import { SyncTeamGroupUsersInteractor } from './domain/interactors/sync-team-group-users.interactor';
import { AddTeamMembersInteractor } from './domain/interactors/add-team-members.interactor';
import { CreateTeamMicrosoftSitesInteractor } from './domain/interactors/create-team-microsoft-sites.interactor';
import { GetTeamPublicCacheByIdInteractor } from './domain/interactors/get-team-public-cache-by-id.interactor';
import { LeaveTeamDevInteractor } from './domain/interactors/leave-team-dev.interactor';
import { Subject } from 'rxjs';
import { ChangeTeamRootInteractor } from './domain/interactors/change-team-root.interactor';
import { OperationLogsProvider } from '../operation-logs/operation-logs.provider';
import { CommonProvider } from '../common/common.provider';
import { CreateTeamTagInteractor } from './domain/interactors/create-team-tag.interactor';
import { GetTeamTagsInteractor } from './domain/interactors/get-team-tags.interactor';
import { UpdateTeamTagInteractor } from './domain/interactors/update-team-tag.interactor';

export abstract class TeamProvider {
  abstract provideAcceptInvite(): AcceptInviteInteractor;
  abstract provideAddTeamMember(): AddTeamMemberInteractor;
  abstract provideAddTeamMembers(): AddTeamMembersInteractor;
  abstract provideCanEnableDomain(): CanEnableDomainInteractor;
  abstract provideChangeTeamRoot(): ChangeTeamRootInteractor;
  abstract provideCreateTeam(): CreateTeamInteractor;
  abstract provideCreateTeamGroup(): CreateTeamGroupInteractor;
  abstract provideCreateTeamMicrosoftSites(): CreateTeamMicrosoftSitesInteractor;
  abstract provideDeleteTeamGroup(): DeleteTeamGroupInteractor;
  abstract provideDisableDomain(): DisableDomainInteractor;
  abstract provideEnableDomain(): EnableDomainInteractor;
  abstract provideGetTeamById(): GetTeamByIdInteractor;
  abstract provideGetTeamGroups(): GetTeamGroupsInteractor;
  abstract provideGetTeamPublicCacheById(): GetTeamPublicCacheByIdInteractor;
  abstract provideGetTeamTags(): GetTeamTagsInteractor;
  abstract provideGetTeamSize(): GetTeamSizeInteractor;
  abstract provideGetTeamUsers(): GetTeamUsersInteractor;
  abstract provideLeaveTeam(): LeaveTeamInteractor;
  abstract provideLeaveTeamDev(): LeaveTeamDevInteractor;
  abstract provideRemoveTeamMember(): RemoveTeamMemberInteractor;
  abstract provideSetTeamTag(): CreateTeamTagInteractor;
  abstract provideSyncTeamGroupUsers(): SyncTeamGroupUsersInteractor;
  abstract provideUpdateTeam(): UpdateTeamInteractor;
  abstract provideUpdateTeamGroup(): UpdateTeamGroupInteractor;
  abstract provideUpdateTeamSettings(): UpdateTeamSettingsInteractor;
  abstract provideUpdateTeamTag(): UpdateTeamTagInteractor;
  abstract provideUpdateUser(): UpdateUserInteractor;
  abstract provideUpdateUserLocal(): UpdateUserLocalInteractor;
}

export class TeamDefaultProvider implements TeamProvider {
  private readonly albumsCommon = new AlbumCommonDefaultProvider();
  private readonly teamCommon = new TeamCommonDefaultProvider();
  private readonly authCommon = new AuthCommonDefaultProvider();
  private readonly teamCollection: CollectionReference<AnyTeamEntityVersion>;
  private readonly teamGroupsCollection: CollectionReference<AnyTeamGroupEntityVersion>;
  private readonly usersCollection: CollectionReference<AnyUserEntityVersion>;

  constructor(
    private readonly authProvider: AuthProvider,
    private readonly common: CommonProvider,
    private readonly firestore: Firestore,
    private readonly functions: Functions,
    private readonly operationLogsProvider: OperationLogsProvider,
    private readonly teamGetCurrent$: Subject<Team>,
  ) {
    this.teamCollection = getCollectionRef(firestore, Collections.Teams);
    this.teamGroupsCollection = getCollectionRef(firestore, Collections.TeamGroups);
    this.usersCollection = getCollectionRef(firestore, Collections.Users);
  }

  @Cached()
  public provideAcceptInvite(): AcceptInviteInteractor {
    return new AcceptInviteInteractor(this.functions);
  }

  @Cached()
  public provideAddTeamMember(): AddTeamMemberInteractor {
    return new AddTeamMemberInteractor(
      this.albumsCommon.provideAlbumDtoToAlbumMapper(),
      this.functions,
      this.authProvider.provideGetAuthDto(),
      this.authProvider.provideGetUserById(),
      this.albumsCommon.provideSharingResultDtoToSharingResultMapper(),
    );
  }

  @Cached()
  public provideAddTeamMembers(): AddTeamMembersInteractor {
    return new AddTeamMembersInteractor(this.functions, this.authProvider.provideGetAuthDto());
  }

  @Cached()
  public provideCanEnableDomain(): CanEnableDomainInteractor {
    return new CanEnableDomainInteractor(this.functions);
  }

  @Cached()
  public provideChangeTeamRoot(): ChangeTeamRootInteractor {
    return new ChangeTeamRootInteractor(
      this.operationLogsProvider.provideCreateOperationLog(),
      this.firestore,
      this.functions,
      this.authProvider.provideGetAuthDto(),
      this.common.provideLogger().withTag('ChangeTeamRootInteractor'),
    );
  }

  @Cached()
  public provideCreateTeam(): CreateTeamInteractor {
    return new CreateTeamInteractor(
      this.functions,
      this.authProvider.provideGetAuthDto(),
      this.provideGetTeamById(),
      this.teamGetCurrent$,
    );
  }

  @Cached()
  public provideCreateTeamMicrosoftSites(): CreateTeamMicrosoftSitesInteractor {
    return new CreateTeamMicrosoftSitesInteractor(
      this.functions,
      this.authProvider.provideGetAuthDto(),
      this.provideGetTeamById(),
    );
  }

  @Cached()
  public provideCreateTeamGroup(): CreateTeamGroupInteractor {
    return new CreateTeamGroupInteractor(
      this.firestore,
      this.teamCommon.provideTeamGroupEntityToTeamGroupMapper(),
    );
  }

  @Cached()
  public provideDeleteTeamGroup(): DeleteTeamGroupInteractor {
    return new DeleteTeamGroupInteractor(this.firestore);
  }

  @Cached()
  public provideDisableDomain(): DisableDomainInteractor {
    return new DisableDomainInteractor(this.functions);
  }

  @Cached()
  public provideEnableDomain(): EnableDomainInteractor {
    return new EnableDomainInteractor(this.functions, this.provideUpdateTeamSettings());
  }

  @Cached()
  public provideGetTeamById(): GetTeamByIdInteractor {
    return new GetTeamByIdInteractor(
      this.firestore,
      this.teamCommon.provideTeamEntityToTeamMapper(),
    );
  }

  @Cached()
  public provideGetTeamGroups(): GetTeamGroupsInteractor {
    return new GetTeamGroupsInteractor(
      this.teamGroupsCollection,
      this.teamCommon.provideTeamGroupEntityToTeamGroupMapper(),
    );
  }

  @Cached()
  public provideGetTeamPublicCacheById(): GetTeamPublicCacheByIdInteractor {
    return new GetTeamPublicCacheByIdInteractor(
      this.firestore,
      this.teamCommon.provideTeamPublicCacheEntityToTeamPublicCacheMapper(),
    );
  }

  @Cached()
  public provideGetTeamTags(): GetTeamTagsInteractor {
    return new GetTeamTagsInteractor(this.firestore);
  }

  @Cached()
  public provideGetTeamSize(): GetTeamSizeInteractor {
    return new GetTeamSizeInteractor(this.firestore);
  }

  @Cached()
  public provideGetTeamUsers(): GetTeamUsersInteractor {
    return new GetTeamUsersInteractor(
      this.firestore,
      this.authProvider.provideGetUser(),
      this.authCommon.provideUserEntityToUserMapper(),
    );
  }

  @Cached()
  public provideLeaveTeam(): LeaveTeamInteractor {
    return new LeaveTeamInteractor(this.functions);
  }

  @Cached()
  public provideLeaveTeamDev(): LeaveTeamDevInteractor {
    return new LeaveTeamDevInteractor(this.functions);
  }

  @Cached()
  public provideRemoveTeamMember(): RemoveTeamMemberInteractor {
    return new RemoveTeamMemberInteractor(this.functions);
  }

  @Cached()
  public provideSetTeamTag(): CreateTeamTagInteractor {
    return new CreateTeamTagInteractor(this.firestore);
  }

  @Cached()
  public provideSyncTeamGroupUsers(): SyncTeamGroupUsersInteractor {
    return new SyncTeamGroupUsersInteractor(this.functions, this.authProvider.provideGetAuthDto());
  }

  @Cached()
  public provideUpdateTeamGroup(): UpdateTeamGroupInteractor {
    return new UpdateTeamGroupInteractor(
      this.firestore,
      this.teamCommon.provideTeamGroupEntityToTeamGroupMapper(),
    );
  }

  @Cached()
  public provideUpdateTeamSettings(): UpdateTeamSettingsInteractor {
    return new UpdateTeamSettingsInteractor(this.teamCollection);
  }

  @Cached()
  public provideUpdateTeamTag(): UpdateTeamTagInteractor {
    return new UpdateTeamTagInteractor(this.firestore);
  }

  @Cached()
  public provideUpdateTeam(): UpdateTeamInteractor {
    return new UpdateTeamInteractor(this.teamCollection, this.provideGetTeamById());
  }

  @Cached()
  public provideUpdateUser(): UpdateUserInteractor {
    return new UpdateUserInteractor(
      this.albumsCommon.provideAlbumDtoToAlbumMapper(),
      this.functions,
      this.authProvider.provideGetAuthDto(),
      this.authProvider.provideGetUserById(),
      this.albumsCommon.provideSharingResultDtoToSharingResultMapper(),
    );
  }

  @Cached()
  public provideUpdateUserLocal(): UpdateUserLocalInteractor {
    return new UpdateUserLocalInteractor(this.usersCollection);
  }
}
